import { useEffect, useState } from "react";
import { CheckCircle2, Download, KeyRound, RefreshCw } from "lucide-react";
import { api, patch, post } from "../../api";
import { Brand, useActionDialog } from "../../components/ui";

interface UpdateInfo {
  installed: string;
  available?: string;
  updateAvailable?: boolean;
  architecture: string;
  repository: string;
  tokenConfigured: boolean;
  publishedAt?: string;
}

export function UpdateCenter({ build }: { build: { version: string; commit: string; buildDate: string } }) {
  const { confirm: confirmAction, host: dialogHost } = useActionDialog();
  const [info, setInfo] = useState<UpdateInfo | null>(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void api<UpdateInfo>("/system/update").then(setInfo).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "Não foi possível carregar as atualizações."));
  }, []);

  const check = async () => {
    setBusy("check"); setError(""); setMessage("");
    try {
      const next = await post<UpdateInfo>("/system/update/check", {});
      setInfo(next);
      setMessage(next.updateAvailable ? "Uma nova versão está disponível." : "O painel está atualizado.");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Não foi possível verificar a versão."); }
    finally { setBusy(""); }
  };

  const saveToken = async (clearToken = false) => {
    setBusy("token"); setError(""); setMessage("");
    try {
      const next = await patch<{ repository: string; tokenConfigured: boolean }>("/system/update", { newToken: token, clearToken });
      setInfo(current => current ? { ...current, ...next } : null);
      setToken(""); setMessage(clearToken ? "Token removido." : "Token protegido e salvo.");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Não foi possível salvar o token."); }
    finally { setBusy(""); }
  };

  const apply = async () => {
    if (!await confirmAction({ title: `Atualizar para ${info?.available}?`, description: "Os serviços serão reiniciados automaticamente. Acompanhe o progresso em Atividades.", confirmLabel: "Confirmar atualização" })) return;
    setBusy("apply"); setError(""); setMessage("");
    try {
      const result = await post<{ jobId: string }>("/system/update/apply", {});
      setMessage(`Atualização iniciada (${result.jobId.slice(0, 8)}). Acompanhe em Atividades; esta página pode reconectar durante o reinício.`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Não foi possível iniciar a atualização."); setBusy(""); }
  };

  const installed = info?.installed || build.version;
  const checked = Boolean(info?.available);
  const updateState = info?.updateAvailable ? "available" : checked ? "current" : "pending";
  return <><section className="panel update-center">
    <div className="update-hero">
      <Brand compact />
      <div><span className="eyebrow">SOBRE · ATUALIZAÇÕES</span><h2>Reborn Valheim Control</h2><p>Versão {installed}</p></div>
      <div className={`update-state ${updateState}`}>{info?.updateAvailable ? <Download size={18}/> : checked ? <CheckCircle2 size={18}/> : <RefreshCw size={18}/>}<span><b>{info?.updateAvailable ? `${info.available} disponível` : checked ? "Você está atualizado" : "Verifique a versão"}</b><small>{info?.updateAvailable ? "Backup e reinício seguros" : checked ? "Última consulta concluída" : "Consulte o GitHub Release configurado"}</small></span></div>
    </div>
    <dl className="config-list update-facts"><div><dt>Instalada</dt><dd>{installed}</dd></div><div><dt>Disponível</dt><dd>{info?.available || "Verifique agora"}</dd></div><div><dt>Arquitetura</dt><dd>{info?.architecture || "linux-amd64"}</dd></div><div><dt>Commit</dt><dd>{build.commit ? build.commit.slice(0, 7) : "development"}</dd></div></dl>
    <div className="inline-actions"><button className="secondary small" disabled={Boolean(busy)} onClick={() => void check()}><RefreshCw className={busy === "check" ? "spin" : ""} size={15}/>{busy === "check" ? "Verificando…" : "Verificar"}</button><button className="primary small" disabled={Boolean(busy) || !info?.updateAvailable} onClick={() => void apply()}><Download size={15}/>{busy === "apply" ? "Atualizando…" : "Atualizar painel"}</button></div>
    <details className="update-credentials"><summary>Credencial do repositório</summary><div className="update-token"><KeyRound size={17}/><label className="field"><span>Token do GitHub {info?.tokenConfigured ? "configurado" : "(necessário para repositório privado)"}</span><input type="password" value={token} onChange={event => setToken(event.target.value)} autoComplete="off" placeholder="github_pat_…"/></label><button className="secondary small" disabled={busy === "token" || !token} onClick={() => void saveToken()}>Salvar token</button>{info?.tokenConfigured && <button className="text-button" disabled={busy === "token"} onClick={() => void saveToken(true)}>Remover</button>}</div></details>
    <small className="update-source">Fonte fixa: {info?.repository || "reborndec/reborn-valheim"}. O navegador não pode fornecer URLs de download.</small>
    {message && <div className="notice success">{message}</div>}{error && <div className="notice error">{error}</div>}
  </section>{dialogHost}</>;
}
